import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { testimonialService } from '../../../services/testimonialService';

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTestimonials();
    const unsubscribe = testimonialService?.subscribeToChanges((payload) => {
      if (['INSERT', 'UPDATE', 'DELETE'].includes(payload?.eventType)) {
        loadTestimonials(); 
      }
    });
    return () => unsubscribe();
  }, []);

  const loadTestimonials = async () => {
    try {
      const data = await testimonialService?.getAll();
      if (!data || data.length === 0) {
        setTestimonials([
          {
            id: 1,
            rating: 5,
            text: "ORAZEK a transformé notre gestion logistique. Leur réactivité et leur professionnalisme sont inégalés à Kinshasa.",
            services: ["Logistique", "Transport"],
            name: "Jean-Paul M.",
            position: "Directeur Opérations",
            company: "Kibali Goldmines",
            image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200",
            logo: "/assets/images/logos/client1.png"
          },
          {
            id: 2,
            rating: 5,
            text: "L'équipe IT d'ORAZEK nous a aidés à sécuriser l'ensemble de notre infrastructure réseau.",
            services: ["Cybersécurité"],
            name: "Sarah K.",
            position: "CTO",
            company: "Equity Bank",
            image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=200",
            logo: "/assets/images/logos/client2.png"
          }
        ]);
      } else {
        setTestimonials(data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  const currentTestimonial = testimonials[currentIndex];

  if (loading) return <div className="py-20 bg-white dark:bg-slate-900 text-center text-slate-900 dark:text-white">Chargement...</div>;

  return (
    <section className="py-20 lg:py-32 bg-white dark:bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-white dark:bg-slate-900" />
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px]" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[100px]" />

      {/* CONTAINER ÉLARGI : max-w-[1920px] */}
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 space-y-4">
          
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-full">
            <Icon name="MessageSquare" size={16} className="text-blue-500 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300 uppercase tracking-wider">Témoignages</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white tracking-tight">
            La confiance de nos <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">partenaires</span>
          </h2>
        </motion.div>

        <div className="relative max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 50, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -50, scale: 0.95 }}
              transition={{ duration: 0.5 }}
              className="bg-slate-50 dark:bg-slate-800/60 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl">

              <div className="grid grid-cols-1 lg:grid-cols-5 min-h-[500px]">
                {/* Gauche : Texte */}
                <div className="lg:col-span-3 p-10 md:p-16 flex flex-col justify-between">
                  <div>
                    <div className="flex gap-2 mb-8">
                      {[...Array(5)].map((_, i) => (
                        <Icon key={i} name="Star" size={20} className={`${i < currentTestimonial?.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300 dark:text-slate-600'}`} />
                      ))}
                    </div>
                    <Icon name="Quote" size={48} className="text-blue-500/20 mb-4" />
                    <p className="text-2xl md:text-3xl text-slate-700 dark:text-slate-200 font-medium leading-relaxed italic">
                      "{currentTestimonial?.text}"
                    </p>
                  </div>
                  <div className="mt-12 pt-10 border-t border-slate-200 dark:border-white/10 flex items-center gap-6">
                    <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-blue-500/30">
                      <Image src={currentTestimonial?.image} alt={currentTestimonial?.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-slate-900 dark:text-white">{currentTestimonial?.name}</h4>
                      <div className="text-base text-slate-600 dark:text-slate-400">{currentTestimonial?.position}</div>
                    </div>
                  </div>
                </div>

                {/* Droite : Logo */}
                <div className="lg:col-span-2 bg-gradient-to-br from-blue-700 to-blue-900 p-16 flex items-center justify-center relative overflow-hidden">
                   <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                   
                   {/* Cercle déco */}
                   <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0 border-[50px] border-white/5 rounded-full scale-150 border-dashed"
                   />

                   <div className="relative w-56 h-56 bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-8 flex items-center justify-center shadow-2xl rotate-3 transform hover:scale-105 transition-transform duration-500">
                      <Image src={currentTestimonial?.logo} alt="Logo" className="w-full h-full object-contain filter drop-shadow-xl" />
                   </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-center gap-6 mt-12">
            <button onClick={prevTestimonial} className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-blue-400 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 transition-all duration-300 transform hover:scale-110"><Icon name="ArrowLeft" size={24} /></button>
            <button onClick={nextTestimonial} className="p-4 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-blue-400 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 transition-all duration-300 transform hover:scale-110"><Icon name="ArrowRight" size={24} /></button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;